export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface ExportOptions {
  sessionTitle: string;
  sessionId: string;
  createdAt: string;
  messages: Message[];
  reportOptions?: {
    length: string;
    detail: string;
    citation: string;
  };
}

/**
 * Export a chat session to a markdown file
 */
export function exportSessionToMarkdown(options: ExportOptions): void {
  // Build markdown content
  let markdown = `# ${options.sessionTitle}\n\n`;
  markdown += `**Session ID:** ${options.sessionId}\n`;
  markdown += `**Created:** ${new Date(options.createdAt).toLocaleString()}\n`;
  markdown += `**Messages:** ${options.messages.length}\n\n`;

  if (options.reportOptions) {
    markdown += `**Report Settings:**\n`;
    markdown += `- Length: ${options.reportOptions.length}\n`;
    markdown += `- Detail: ${options.reportOptions.detail}\n`;
    markdown += `- Citations: ${options.reportOptions.citation}\n\n`;
  }

  markdown += `---\n\n`;

  // Add messages
  options.messages.forEach(msg => {
    const timestamp = new Date(msg.timestamp).toLocaleString();
    const role = msg.role === 'user' ? '👤 User' : msg.role === 'assistant' ? '🤖 Assistant' : '⚙️ System';
    markdown += `## ${role} - ${timestamp}\n\n`;
    markdown += `${msg.content}\n\n`;
    markdown += `---\n\n`;
  });

  // Trigger download
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitizeFilename(options.sessionTitle)}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export a single message to markdown
 */
export function exportMessageToMarkdown(
  message: Message,
  sessionTitle: string = 'Chat Export'
): void {
  const timestamp = new Date(message.timestamp).toLocaleString();
  const role = message.role === 'user' ? '👤 User' : message.role === 'assistant' ? '🤖 Assistant' : '⚙️ System';

  let markdown = `# ${sessionTitle}\n\n`;
  markdown += `## ${role} - ${timestamp}\n\n`;
  markdown += `${message.content}\n`;

  // Trigger download
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = `${sanitizeFilename(sessionTitle)}_${message.id}.md`;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Sanitize filename to remove invalid characters
 */
function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-z0-9_\-\s]/gi, '_')
    .replace(/\s+/g, '_')
    .toLowerCase()
    .substring(0, 100); // Limit length
}
